import React, { useEffect, useState } from "react";
import { openKkiapayWidget, addKkiapayCloseListener } from "kkiapay";
import { Link, useLocation } from "react-router-dom";
import Layout, { Main } from "../Layout";
import {
  Alert,
  Button,
  Card,
  CardText,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import Switch from "../Switch";
import CurrencyConverter, {
  ConvertEuroToXof,
} from "../utils/CurrencyConverter";
import { useAsyncFn, useLocalStorage } from "react-use";
import { useApi } from "../Api/ApiContext";
import { Price } from "./Celebrity";
import styled from "styled-components";

const KkiapayLink = styled.a`
  border-radius: 10px;
  overflow: hidden;
  display: block;
  cursor: pointer;
  width: 100%;
  max-width: 150px;
  margin: auto;
  img {
    border: solid 1px ${({ theme }) => theme.card};
    border-radius: 10px;
    &:hover {
      border-width: 2px;
    }
  }
`;

const PaymentCardImageWrapper = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: row;
  gap: 15px;
`;

const PaymentCardImage = styled.div`
  width: 50px;
`;

const StripeLogo = styled.div`
  width: 180px;
  margin: auto;
  img {
    width: 100%;
  }
`;

const OrderSummary = () => {
  const [orderData, setOrderData, removeOrderData] =
    useLocalStorage("_order_data");

  const { cbPayment } = useApi();
  const location = useLocation();
  const product = orderData?.product;
  const celebrity = orderData?.celebrity;
  const order = orderData?.order;
  const orderResponse = orderData?.orderResponse;
  const [open, setOpen] = useState(false);
  const [termAccepted, setTermAccepted] = useState(false);
  const toggle = () => setOpen(!open);
  const [openWidget, setOpenWidget] = useState();

  const [
    { loading: cbLoading, error: cbError, value: cbResponse },
    makeCbPayment,
  ] = useAsyncFn((paymentParams) => cbPayment(paymentParams));

  const [isEuro, setIsEuro] = useState(
    location.state?.currency === "euro"
      ? true
      : location.state?.currency !== "cfa"
  );

  useEffect(() => {
    addKkiapayCloseListener(() => {
      setOpenWidget(false);
    });
  }, [openWidget]);

  const handleKKiaPay = () => {
    setOpenWidget(true);
  };

  const handleCBPayment = (e) => {
    e.preventDefault();
    setOpen(false);
    makeCbPayment({
      orderRef: orderResponse.ref,
      paymentPartner: "stripe",
      paymentCurrency: isEuro ? "euro" : "cfa",
    });
  };

  const handleTermAccepted = () => {
    setTermAccepted(!termAccepted);
  };

  const handleClose = (e) => {
    setOpen(false);
  };

  if (!order || !product || !celebrity) {
    return (
      <Layout>
        <Row>
          <Col lg={6} className={"mx-auto"}>
            <Alert color="danger">
              <p>Une erreur s'est produite lors de la commande.</p>
              <p>Merci de réessayer plus tard.</p>
              <Row
                className={"justify-content-center justify-content-md-end mt-3"}
              >
                <Col md={4}>
                  <Link to={"/"} className={"btn btn-primary"}>
                    Retour à l'accueil
                  </Link>
                </Col>
              </Row>
            </Alert>
          </Col>
        </Row>
      </Layout>
    );
  }

  if (cbLoading) {
    return (
      <Layout>
        <Row>
          <Col lg={6} className={"mx-auto"}>
            <Spinner color={"danger"} />
          </Col>
        </Row>
      </Layout>
    );
  }
  if (cbResponse) {
    window.location.href = cbResponse.paymentUrl;
    return (
      <Layout>
        <Row>
          <Col lg={6} className="mx-auto">
            <Spinner color={"danger"} />
          </Col>
        </Row>
      </Layout>
    );
  }
  if (openWidget) {
    openKkiapayWidget({
      amount:
        product.currency === "cfa"
          ? orderResponse.price
          : ConvertEuroToXof(orderResponse.price),
      position: "right",
      callback: `${process.env.REACT_APP_FRONT_BASE_URL}/shop/order/confirm/${orderResponse.ref}`,
      data: {
        orderRef: orderResponse.ref,
      },
      theme: "green",
      sandbox: "true",
      paymentmethod: "momo",
      key: process.env.REACT_APP_KKIAPAY_API_KEY,
    });
  }

  return (
    <Layout
      backPath={{
        pathname: `/shop/${celebrity.id}/booking`,
      }}
    >
      <Main className="my-lg-3 my-3">
        {cbError && (
          <Row>
            <Col lg={6} className={"mx-auto"}>
              <Alert color="danger">
                Une erreur s'est produite lors de la commande. Merci de
                réessayer plus tard.
              </Alert>
            </Col>
          </Row>
        )}
        <Row className={"mt-lg-2 mt-2"}>
          <Col lg={6} className={"mx-auto"}>
            <h1 className={"fs-4"}>Récapitulatif de votre commande</h1>
          </Col>
        </Row>
        <Row className={"mt-lg-2 mt-2"}>
          <Col lg={6} className="mx-auto">
            <Card body>
              <CardTitle tag="h6" className={"fw-bold text-capitalize"}>
                {product.category.name}
              </CardTitle>
              <CardText>
                {product.category.slug.toLowerCase() !== "dedicace-musicale" ? (
                  <p>
                    Livré en :{" "}
                    <span className={"fw-bold"}>{celebrity.delay} heures</span>
                  </p>
                ) : (
                  <p>
                    Date indicative de sortie de la chanson :{" "}
                    <span className={"fw-bold"}>
                      {new Date(product.launchDate).toLocaleDateString()}
                    </span>
                  </p>
                )}
                <Row className={"d-flex align-items-baseline"}>
                  <Col sm={1}>
                    <Switch
                      state={isEuro}
                      setState={setIsEuro}
                      trueLabel={"EURO"}
                      falseLabel={"CFA"}
                    />
                  </Col>
                  <Col sm={{ size: 3, offset: 4 }}>
                    <Price className={"fw-bold text-center fs-4"}>
                      {CurrencyConverter(
                        product.price,
                        product.currency,
                        isEuro ? "euro" : "cfa"
                      )}
                    </Price>
                  </Col>
                </Row>
              </CardText>
            </Card>
          </Col>
        </Row>
        <Row className={"mt-4"}>
          <Col lg={6} className="mx-auto">
            <div>
              <span className="fw-bold">Dédicace demandée par :</span>{" "}
              <div>{order?.dedicaceFrom}</div>
            </div>
          </Col>
        </Row>
        <Row className={"mt-3"}>
          <Col lg={6} className="mx-auto">
            <div>
              <span className="fw-bold">Dédicace demandée pour :</span>{" "}
              <div>{order?.dedicaceTo}</div>
            </div>
          </Col>
        </Row>
        <Row className={"mt-3"}>
          <Col lg={6} className="mx-auto">
            <div>
              <span className="fw-bold">Motif de la demande :</span>
              <div>{order?.event}</div>
            </div>
          </Col>
        </Row>
        <Row className={"mt-3"}>
          <Col lg={6} className="mx-auto">
            <div>
              <span className="fw-bold">Description de la demande :</span>{" "}
              <div>{order?.comment}</div>
            </div>
          </Col>
        </Row>
        <Row className={"mt-3"}>
          <Col lg={6} className="mx-auto">
            <div>
              <span className="fw-bold">
                Adresse e-mail de livraison de commande :
              </span>
              <div>{order?.deliveryEmail}</div>
            </div>
          </Col>
        </Row>
      </Main>
      <Row className={"justify-content-center mx-auto mt-4 mb-1"}>
        <Col xs={12} md={12} className={"mx-auto text-center"}>
          <Button
            className={"w-100 py-2 fw-bold fs-5 mt-3"}
            color={"success"}
            onClick={(e) => setOpen(true)}
          >
            Carte bancaire
          </Button>
        </Col>
      </Row>
      <StripeLogo>
        <img src="/cbImage.png" alt="image logo stripe" />
      </StripeLogo>

      <Row className={"justify-content-center mx-auto my-4"}>
        <Col xs={12} md={12} className={"mx-auto text-center"}>
          <KkiapayLink
            className={"py-2 fw-bold mt-3 fs-4"}
            color={"success"}
            onClick={handleKKiaPay}
          >
            <img
              className={"w-100"}
              src="/kkiapayImage.png"
              alt="logo kkiapay"
            />
          </KkiapayLink>
          <small className={"text-secondary"}>
            Payer avec votre numero de téléphone via votre opérateur
            téléphonique.
          </small>
          <div>
            <small className="text-success fw-bold">Paiement sécurisé</small>
          </div>
          <PaymentCardImageWrapper>
            <PaymentCardImage>
              <img
                className={"w-100"}
                src="/mtn_momo_pay.jpeg"
                alt="logo mtn_momopay"
              />
            </PaymentCardImage>
            <PaymentCardImage>
              <img
                className={"w-100"}
                src="/moov_money.png"
                alt="logo moov_money"
              />
            </PaymentCardImage>
          </PaymentCardImageWrapper>
        </Col>
      </Row>
      <Modal isOpen={open} toggle={toggle} size={"lg"}>
        <ModalHeader toggle={toggle}>Information</ModalHeader>
        <ModalBody>
          Vous allez être redirigé vers le site de notre partenaire pour le
          paiement.
          <ModalFooter className={"d-flex justify-content-start"}>
            <FormGroup row>
              <Col xs={1}>
                <Input
                  id={"term"}
                  type={"checkbox"}
                  checked={termAccepted}
                  onChange={handleTermAccepted}
                />
              </Col>
              <Col xs={10} sm={11}>
                <Label for={"term"}>
                  J'accepte les{" "}
                  <Link to="#">conditions générales de ventes</Link> de
                  @llodedicace.
                </Label>
              </Col>
            </FormGroup>
          </ModalFooter>
          <Row className={"justify-content-sm-around"}>
            <Button className={"col-12 col-sm-2"} onClick={handleClose}>
              Annuler
            </Button>
            <Button
              className={"col-12 col-sm-2 mt-2 mt-sm-0"}
              color={"primary"}
              onClick={(e) => handleCBPayment(e)}
              disabled={!termAccepted}
            >
              Continuer
            </Button>
          </Row>
        </ModalBody>
      </Modal>
    </Layout>
  );
};

export default OrderSummary;
